'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { X } from 'lucide-react'
import type { ContactOrUser } from '@/components/share-room/types'

/** Same shape as share modal selection rows (`ContactOrUser`; `accountName` optional). */
export type CommunityInviteRecipientRow = ContactOrUser

type CommunityInviteRecipientSummaryProps = {
  recipients: CommunityInviteRecipientRow[]
  onRemove: (id: string) => void
  /** Max badges before “and N more” (default 6). */
  previewCount?: number
}

export function CommunityInviteRecipientSummary({
  recipients,
  onRemove,
  previewCount = 6,
}: CommunityInviteRecipientSummaryProps) {
  const [expanded, setExpanded] = useState(false)

  if (recipients.length === 0) return null

  const preview = recipients.slice(0, previewCount)
  const restCount = Math.max(0, recipients.length - previewCount)

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-slate-800">
        {recipients.length} {recipients.length === 1 ? 'person' : 'people'} will be invited
      </p>
      <div className="flex flex-wrap gap-1">
        {preview.map((item) => (
          <div
            key={item.id}
            className={`flex items-center rounded-full px-2 py-0.5 text-xs ${
              item.type === 'user' ? 'bg-gray-100 text-gray-800' : 'bg-green-500 text-white'
            }`}
          >
            <Avatar className="mr-1 h-4 w-4">
              <AvatarImage
                src={item.image || `https://api.dicebear.com/6.x/initials/svg?seed=${item.firstName} ${item.lastName}`}
                alt=""
              />
              <AvatarFallback className="text-[10px]">
                {item.firstName[0]}
                {item.lastName[0]}
              </AvatarFallback>
            </Avatar>
            <span>
              {item.firstName} {item.lastName}
            </span>
            <button
              type="button"
              onClick={() => onRemove(item.id)}
              className={`ml-1 rounded-full p-0.5 ${item.type === 'user' ? 'hover:bg-gray-200' : 'hover:bg-green-600'}`}
              aria-label={`Remove ${item.firstName} ${item.lastName}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      {restCount > 0 && !expanded && (
        <p className="text-sm text-slate-600">
          and {restCount} more {restCount === 1 ? 'person' : 'people'}
        </p>
      )}
      {recipients.length > previewCount && (
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm text-blue-700"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? 'Show less' : `Show all ${recipients.length} names`}
        </Button>
      )}
      {expanded && (
        <ul className="max-h-48 space-y-1.5 overflow-y-auto rounded-md border border-slate-200 bg-slate-50/50 p-3 text-sm">
          {recipients.map((item) => (
            <li key={item.id} className="flex items-start justify-between gap-3">
              <span className="min-w-0 break-words text-slate-800">
                <span className="font-medium">
                  {item.firstName} {item.lastName}
                </span>
                <span className="block text-xs text-slate-500">{item.email}</span>
              </span>
              <button
                type="button"
                onClick={() => onRemove(item.id)}
                className="shrink-0 rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                aria-label={`Remove ${item.firstName} ${item.lastName}`}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
